import type { EntityTableFilterParams } from '@/components/map/utils/entityMapFilters';

import { apiClient } from './apiClient';

/**
 * Hand-written client for the Network Overview table endpoints (`network/table/*`).
 * Membership (Area/DMA) is resolved server-side; these functions pass the hierarchy
 * selectors, pagination and the serialised committed map filters. Search/sort params
 * are added in a later stage.
 */

/**
 * Repeat array params (`leakEntityStatuses=a&leakEntityStatuses=b`) rather than axios's
 * default bracketed form, which FastEndpoints' List binding does not read.
 *
 * Note axios drops empty arrays entirely, so "active but nothing selected" cannot ride on the
 * array — `serialiseEntityFiltersToParams` sends an explicit `*StatusFilter`/`pipeTypeFilter`
 * flag for that case. Without it, "every toggle off" would reach the API as "no filter" and
 * return everything: the exact inverse of the intended result.
 */
const REPEAT_ARRAY_PARAMS = { indexes: null } as const;

export interface PagedResponse<T> {
    items: T[];
    totalCount: number;
    page: number;
    pageSize: number;
    hasPrevious: boolean;
    hasNext: boolean;
}

export interface NetworkTableAreaRow {
    /** Area name, or null for the synthetic Unassigned Area group. */
    name: string | null;
    isUnassigned: boolean;
    /** Distinct resolved DMAs among this Area's POIs (+1 when any resolve to no DMA). */
    dmaCount: number;
    hasUnassignedDma: boolean;
    /** Projected POI entities (root rows) in this Area — not the row count. */
    poiCount: number;
    /** Projected leak entities (child rows + confirmed roots) in this Area. */
    leaksFound: number;
    /** Plain table-record (row) count — what the "Total Network Records" header sums. */
    recordCount?: number;
    potentialSaving: number | null;
    minLng: number | null;
    minLat: number | null;
    maxLng: number | null;
    maxLat: number | null;
}

/**
 * The root level is returned whole — not paged. The largest client has 8 Areas, so a page control
 * never rendered; the DMA and POI levels beneath it stay paged because they are unbounded.
 */
export interface NetworkTableAreasResponse {
    items: NetworkTableAreaRow[];
    /** Total POIs under the current filters — the table's "Total Network Records". */
    totalFilteredPoiCount: number;
}

export interface NetworkTableDmaRow {
    /** DMA ident, or null for the Unassigned DMA group. */
    ident: string | null;
    name: string | null;
    isUnassigned: boolean;
    poiCount: number;
    leaksFound: number;
    potentialSaving: number | null;
    minLng: number | null;
    minLat: number | null;
    maxLng: number | null;
    maxLat: number | null;
}

export interface NetworkTablePoiRow {
    id: string;
    stop: number;
    worklist: string;
    groupId: string;
    /** Canonical map status string (matches the map layer's `status`). */
    status: string;
    leakageResult: number;
    repaired: boolean | null;
    whatThreeWords: string | null;
    leakWhatThreeWords: string | null;
    pipeType: string | null;
    assumedLeakageRate: number;
    measuredLeakageRate: number | null;
    isLeakVisible: boolean | null;
    nodigFix: boolean | null;
    isPlanned: boolean;
    created: string | null;
    surveyedDate: string | null;
    searchDate: string | null;
    longitude: number;
    latitude: number;
    /** "21-1" for child leak rows; null for root rows. */
    suffixLabel?: string | null;
    /** Split-model POI-entity status; null for child rows. */
    poiEntityStatus?: string | null;
    /** Split-model leak-entity status; null when the row projects none. */
    leakEntityStatus?: string | null;
    /**
     * The map feature this row's Locate/Details actions target under the entity contract —
     * a navigation aid, not a claim about what the row is. Opaque; never parsed client-side.
     */
    mapEntityId?: string;
    /** 'poi' or 'leak' — which family mapEntityId belongs to. */
    mapEntityKind?: string;
}

/** Addresses an Area group: a named area, or the synthetic Unassigned Area bucket. */
export type AreaSelector = { type: 'assigned'; name: string } | { type: 'unassigned' };

/** Addresses a DMA group within an Area: a named DMA, or the Unassigned DMA bucket. */
export type DmaSelector = { type: 'assigned'; ident: string } | { type: 'unassigned' };

export interface PageParams {
    page: number;
    pageSize: number;
}

/** Filters, search and sort — shared by every level. The root level adds no pagination. */
export interface NetworkTableFilterQueryParams {
    /** From `serialiseEntityFiltersToParams`; `{}` when filters are inactive. */
    filters?: EntityTableFilterParams;
    /** Free-text term; omit (or undefined) when not searching. */
    search?: string;
    /** Whitelisted per level by the endpoint validators; omit for the level's default ordering. */
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
}

/** The paged levels (DMAs beneath an Area, POIs beneath a DMA) — both unbounded. */
export interface NetworkTablePagedQueryParams extends NetworkTableFilterQueryParams, PageParams {}

function areaSelectorParams(selector: AreaSelector): Record<string, string | boolean> {
    return selector.type === 'assigned' ? { areaName: selector.name } : { unassignedArea: true };
}

function dmaSelectorParams(selector: DmaSelector): Record<string, string | boolean> {
    return selector.type === 'assigned' ? { dmaIdent: selector.ident } : { unassigned: true };
}

/** Axios omits undefined values, so an absent search/sort simply does not reach the API. */
function queryParams({ filters = {}, search, sortBy, sortDir }: NetworkTableFilterQueryParams) {
    return { search, sortBy, sortDir, ...filters };
}

export async function fetchNetworkTableAreas(
    clientId: string,
    query: NetworkTableFilterQueryParams,
    signal?: AbortSignal,
): Promise<NetworkTableAreasResponse> {
    const response = await apiClient.get<NetworkTableAreasResponse>('/network/table/areas', {
        params: { clientId, ...queryParams(query) },
        paramsSerializer: REPEAT_ARRAY_PARAMS,
        signal,
    });
    return response.data;
}

export async function fetchNetworkTableDmas(
    clientId: string,
    area: AreaSelector,
    query: NetworkTablePagedQueryParams,
    signal?: AbortSignal,
): Promise<PagedResponse<NetworkTableDmaRow>> {
    const response = await apiClient.get<PagedResponse<NetworkTableDmaRow>>('/network/table/dmas', {
        params: {
            clientId,
            ...areaSelectorParams(area),
            page: query.page,
            pageSize: query.pageSize,
            ...queryParams(query),
        },
        paramsSerializer: REPEAT_ARRAY_PARAMS,
        signal,
    });
    return response.data;
}

export async function fetchNetworkTablePois(
    clientId: string,
    area: AreaSelector,
    dma: DmaSelector,
    query: NetworkTablePagedQueryParams,
    signal?: AbortSignal,
): Promise<PagedResponse<NetworkTablePoiRow>> {
    const response = await apiClient.get<PagedResponse<NetworkTablePoiRow>>('/network/table/pois', {
        params: {
            clientId,
            ...areaSelectorParams(area),
            ...dmaSelectorParams(dma),
            page: query.page,
            pageSize: query.pageSize,
            ...queryParams(query),
        },
        paramsSerializer: REPEAT_ARRAY_PARAMS,
        signal,
    });
    return response.data;
}
